import './App.css';

import {
  ApiKey,
  ApiKeyClient,
  AUTH_KEY,
  GoogleAuthClient,
  User,
  UserClient,
} from '@kalos/kalos-rpc';
import { AlertDialogProvider, ScrollStoreProvider } from '@kalos/ui';
import { googleLogout, GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { utcToZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AuthRouteWrapper } from './components/AuthRouteWrapper';
import { TanStackQueryDevtools } from './components/devtools/TanstackQueryDevtools';
import { KeyboardAwareLayout } from './components/KeyboardAwareLayout';
import { ENDPOINT } from './constants';
import { AuthContext, type AuthContextType, useAuth } from './context/AuthContext';
import { useFeatureFlags } from './featureFlags/context';
import { FeatureFlagLegend } from './featureFlags/FeatureFlagLegend';
import { FeatureFlagProvider } from './featureFlags/FeatureFlagProvider';
import { useUserQuery } from './hooks/react-query/useUserQuery';
import useEnhancedEffect from './hooks/useEnhancedEffect';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useFieldLayout } from './hooks/useMedia';
import { type RouterContext } from './routes/__root';
import { router } from './tanstackRouter';
import { queryClient } from './tools/queryClient';
import { localStorageManager } from './tools/storageManager';

// Constants and clients
const gClient = new GoogleAuthClient(ENDPOINT);
const userClient = new UserClient(ENDPOINT);
const keyClient = new ApiKeyClient(ENDPOINT);
export const KEY_EXPIRY = 'key_expiry';
const SEPARATOR = ' $$$$$ ';
export const GOOGLE_CLIENT_ID =
  '493739388494-17m5c8kv2gl8pbvreotftn6d39602c6j.apps.googleusercontent.com';

// Local storage key for tracking auth failures across tabs
const AUTH_FAILURE_KEY = 'auth_refresh_failure';

/**
 * Handles the token exchange process.
 */
const handleTokenExchange = async (code: string) => {
  try {
    const token = await gClient.GetAuthToken(code);
    if (!token) {
      throw new Error('Token exchange failed');
    }
    const [accessToken, refreshToken, expiryDate] = token.split(SEPARATOR);
    return { accessToken, refreshToken, expiryDate };
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  }
};

const updateAPIKey = async function (
  userID: number,
  key: string,
  refresh: string,
  expiryDate: string,
) {
  const req = ApiKey.create({
    apiUser: userID,
    textId: 'google_auth_token',
  });
  try {
    const keyRes = await keyClient.Get(req);
    if (keyRes) {
      console.log('seems user has a key so lets update it', keyRes);
      req.id = keyRes.id;
      req.apiKey = key;
      req.apiDescription = refresh;
      req.expireDate = expiryDate;
      req.fieldMask = ['ApiKey', 'ApiDescription', 'ExpireDate'];
      await keyClient.Update(req);
    } else {
      console.log('keyRes is null');
      throw new Error('no key');
    }
  } catch (err) {
    console.log('seems that user has no key so we create one', err);
    req.apiKey = key;
    req.apiDescription = refresh;
    req.expireDate = expiryDate;
    await keyClient.Create(req);
  }
};

export let outsideAuthSignout: AuthContextType['signout'] | null = null;
export let outsideAuthUpdateToken: (() => Promise<void>) | null = null;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authChecked, setAuthChecked] = useState(false);
  const [user, setCurrentUser] = useLocalStorage(
    'user',
    useMemo(() => User.create(), []),
  );
  const [, setAccessToken] = useLocalStorage(AUTH_KEY, '');
  const [expiryString, setExpiryDate] = useLocalStorage(KEY_EXPIRY, '');
  // Track token refresh retry attempts
  const [tokenRefreshRetries, setTokenRefreshRetries] = useState(0);
  const MAX_TOKEN_REFRESH_RETRIES = 3;
  const refreshInFlight = useRef<Promise<void> | null>(null);

  const queryClient = useQueryClient();
  const currentUserQuery = useUserQuery({
    filters: { id: user.id },
    enabled: !!user.id,
  });

  useEffect(() => {
    if (currentUserQuery.isSuccess) {
      setCurrentUser(currentUserQuery.data);
    }
  }, [currentUserQuery.data, currentUserQuery.isSuccess, setCurrentUser]);

  const signin = useCallback(
    async (code: string, cb?: VoidFunction) => {
      try {
        console.log({ code });
        const { accessToken, refreshToken, expiryDate } = await handleTokenExchange(code);
        console.log({ accessToken });
        const userID = await userClient.VerifyGoogleToken(accessToken);
        setAccessToken(accessToken);
        setExpiryDate(expiryDate);
        await updateAPIKey(userID, accessToken, refreshToken, expiryDate);
        const userResult = await userClient.Get(User.create({ id: userID }));
        setCurrentUser(userResult);

        // After successful login, navigate using callback or default
        if (cb) {
          cb();
        } else {
          // Default navigation if no callback provided
          window.location.href = '/home';
        }
      } catch (err) {
        console.error('user was not found in Kalos system', err);
        throw err;
      }
    },
    [setAccessToken, setCurrentUser, setExpiryDate],
  );

  const signout = useCallback(
    (callback?: VoidFunction) => {
      googleLogout();
      localStorage.removeItem(AUTH_KEY);
      localStorage.removeItem(KEY_EXPIRY);
      // Also clear the auth failure marker
      localStorage.removeItem(AUTH_FAILURE_KEY);
      setCurrentUser(User.create());
      setAccessToken('');
      setExpiryDate('');
      queryClient.resetQueries();
      if (callback) {
        callback();
      }
    },
    [queryClient, setAccessToken, setCurrentUser, setExpiryDate],
  );

  const setUser = useCallback(
    (user: User) => {
      setCurrentUser(user);
    },
    [setCurrentUser],
  );

  /**
   * @throws {Error} - If the token exchange fails or payload does not satisfy the expected format.
   */
  const updateToken = useCallback(async () => {
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }
    const refresh = (async () => {
      if (user.id !== 0) {
        try {
          const key = await gClient.RefreshToken(user.id);
          if (key && key.value) {
            const [accessToken, refreshToken, expiryDate] = key.value.split(SEPARATOR);
            setAccessToken(accessToken);
            setExpiryDate(expiryDate);
            await updateAPIKey(user.id, accessToken, refreshToken, expiryDate);
            console.log('Token refreshed successfully');
            // Reset retry count on success
            setTokenRefreshRetries(0);
          } else {
            console.error('No valid token response received');
            throw new Error('failed to refresh token in useEnhancedEffect of AuthProvider');
          }
        } catch (error) {
          console.error('Token refresh error:', error);
          throw error;
        }
      } else {
        throw new Error('Current user is not authenticated');
      }
    })();
    refreshInFlight.current = refresh;
    try {
      await refresh;
    } finally {
      refreshInFlight.current = null;
    }
  }, [setAccessToken, setExpiryDate, user.id]);

  // On load, keep this device's own token while Google still accepts it. The shared api_key
  // row is written by every device of this user; taking its token here put two devices on
  // one session, and the one that came back with the expired copy was signed out.
  useEnhancedEffect(() => {
    if (!authChecked) {
      (async () => {
        const token = localStorageManager.get(AUTH_KEY);
        if (user && user.id !== 0 && token) {
          try {
            await userClient.VerifyGoogleToken(token);
          } catch (err) {
            console.log('stored token was not accepted, refreshing it', err);
            try {
              await updateToken();
            } catch (refreshErr) {
              console.error('error while refreshing token on load', refreshErr);
            }
          }
        }
        setAuthChecked(true);
      })();
    }
  }, [user, authChecked, updateToken]);

  // Setup a window event listener to handle storage changes for cross-tab communication
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === AUTH_FAILURE_KEY && event.newValue === 'true') {
        console.warn(
          'Another tab has reported authentication failure, signing out in this tab as well',
        );
        signout();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [signout]);

  outsideAuthSignout = signout;
  outsideAuthUpdateToken = updateToken;

  useEnhancedEffect(() => {
    const interval = setInterval(
      async () => {
        if (expiryString) {
          const expiryDate = new Date(expiryString);
          const now = new Date();
          // convert the time to EST in case user is in another timezone (expiry is in EST on server)
          const estTime = utcToZonedTime(now, 'America/New_York');
          // Check if the token will expire in the next 10 minutes
          if (expiryDate.getTime() - estTime.getTime() < 10 * 60 * 1000) {
            try {
              await updateToken();
            } catch (err) {
              console.error('Token refresh attempt failed:', err);
              // Increment retry counter
              const newRetryCount = tokenRefreshRetries + 1;
              setTokenRefreshRetries(newRetryCount);

              if (newRetryCount >= MAX_TOKEN_REFRESH_RETRIES) {
                console.error(
                  `Failed to refresh token after ${MAX_TOKEN_REFRESH_RETRIES} attempts. Signing out.`,
                );
                setTokenRefreshRetries(0);

                // Notify other tabs about the authentication failure
                localStorage.setItem(AUTH_FAILURE_KEY, 'true');

                signout();
              } else {
                console.warn(
                  `Token refresh failed. Attempt ${newRetryCount}/${MAX_TOKEN_REFRESH_RETRIES}. Will retry.`,
                );

                // Use exponential backoff for retry with a base of 5 seconds
                const backoffDelay = 5000 * Math.pow(2, newRetryCount - 1);
                console.log(`Scheduling retry in ${backoffDelay / 1000} seconds...`);
                setTimeout(async () => {
                  // Try again after the backoff delay
                  try {
                    await updateToken();
                    console.log('Delayed retry succeeded!');
                  } catch (retryErr) {
                    console.error('Delayed retry attempt also failed:', retryErr);
                    // The retry counter is already updated, so we'll wait for the next interval check
                  }
                }, backoffDelay);
              }
            }
          }
        }
      },
      2.5 * 60 * 1000, // Check every 2.5 minutes
    );

    return () => clearInterval(interval);
  }, [user, expiryString, setAccessToken, setExpiryDate, signout, tokenRefreshRetries]);

  // Also modify to disable the devtools in production and when user is not authenticated
  // @ts-expect-error - Dynamically set property for TanStack Router DevTools
  window.ENABLE_TANSTACK_ROUTER_DEVTOOLS = process.env.NODE_ENV === 'development' && user.id !== 0;

  const value = useMemo(() => {
    return {
      user,
      signin,
      signout,
      setUser,
      authChecked,
    };
  }, [user, signin, signout, setUser, authChecked]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Confirmations, alerts and prompts open as bottom sheets under the field layout, as the
// screens that ask them do. The field layout reads a feature flag, so this sits inside
// FeatureFlagProvider.
function FieldAlertDialogProvider({ children }: { children: React.ReactNode }) {
  const isFieldLayout = useFieldLayout();
  return (
    <AlertDialogProvider presentation={isFieldLayout ? 'sheet' : 'dialog'}>
      {children}
    </AlertDialogProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <KeyboardAwareLayout />
      <ScrollStoreProvider>
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
          <AuthProvider>
            <FeatureFlagProvider>
              <FieldAlertDialogProvider>
                <AppContent />
              </FieldAlertDialogProvider>
            </FeatureFlagProvider>
          </AuthProvider>
        </GoogleOAuthProvider>
      </ScrollStoreProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlags();

  // Create router context
  const routerContext = useMemo<RouterContext>(() => {
    return {
      queryClient,
      authContext: auth,
      featureFlags,
    };
  }, [auth, queryClient, featureFlags]);

  // Only show devtools if:
  // 1. In development mode
  // 2. User is authenticated with valid ID
  // 3. Authentication check is completed
  const showDevTools =
    process.env.NODE_ENV === 'development' && auth.authChecked && auth.user.id !== 0;

  return (
    <AuthRouteWrapper>
      <RouterProvider router={router} context={routerContext} />
      <FeatureFlagLegend />
      {showDevTools && <TanStackQueryDevtools />}
    </AuthRouteWrapper>
  );
}

export default App;
